/**
 * LandingPage — public home page shown to unauthenticated users.
 *
 * If the user is already signed in (MSAL), they are redirected to /dashboard.
 * The "Sign In" CTA triggers the existing Entra loginRedirect flow.
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { loginRequest, signupRequest } from '../auth/authConfig';

import TopNav from '../components/landing/TopNav';
import HeroSection from '../components/landing/HeroSection';
import FeatureGrid from '../components/landing/FeatureGrid';
import TrustSection from '../components/landing/TrustSection';
import Footer from '../components/landing/Footer';

import '../styles/LandingPage.css';

const LandingPage = () => {
    const { instance, inProgress } = useMsal();
    const isAuthenticated = useIsAuthenticated();
    const navigate = useNavigate();
    const [isSigningIn, setIsSigningIn] = useState(false);

    // If already logged in, go straight to home
    useEffect(() => {
        if (isAuthenticated) {
            navigate('/home', { replace: true });
        }
    }, [isAuthenticated, navigate]);

    // Trigger existing Entra login redirect
    const handleSignIn = () => {
        // Guard: prevent multiple simultaneous authentication attempts
        if (inProgress !== InteractionStatus.None || isSigningIn) {
            console.log('[LandingPage] Authentication already in progress');
            return;
        }

        setIsSigningIn(true);
        instance.loginRedirect(loginRequest).catch((err) => {
            console.error('[LandingPage] Sign in error:', err);
            setIsSigningIn(false);
        });
    };

    // Sign up - uses separate flow if configured, otherwise SUSI flow
    const handleSignUp = () => {
        // Guard: prevent multiple simultaneous authentication attempts
        if (inProgress !== InteractionStatus.None || isSigningIn) {
            console.log('[LandingPage] Authentication already in progress');
            return;
        }

        setIsSigningIn(true);
        instance.loginRedirect(signupRequest).catch((err) => {
            console.error('[LandingPage] Sign up error:', err);
            setIsSigningIn(false);
        });
    };

    // Don't flash the landing page for authenticated users
    if (isAuthenticated) return null;

    return (
        <div className="landing-page">
            <TopNav onSignIn={handleSignIn} onSignUp={handleSignUp} />
            <HeroSection onSignIn={handleSignIn} onSignUp={handleSignUp} />
            <FeatureGrid />
            <TrustSection />
            <Footer />
        </div>
    );
};

export default LandingPage;
